import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../api/client';
import { FocusShell } from '../components/layout';
import { Alert, Button, Card, Field, Input } from '@/components/ui';

export const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (token) {
        await api.auth.completeReset({ token, newPassword: password });
        navigate('/signin', { replace: true });
        return;
      }
      await api.auth.startReset(email);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <FocusShell>
      <Card>
        <h1 className="mb-1 text-h2">{token ? 'Set a new password' : 'Reset your password'}</h1>
        <p className="mb-5 text-body text-ink-muted">
          {token
            ? 'Choose a new password. Signing in again on your other devices will be needed.'
            : 'We will email you a link that works once and expires in 30 minutes.'}
        </p>

        {error && (
          <div className="mb-4">
            <Alert tone="warn">{error}</Alert>
          </div>
        )}

        {done ? (
          <Alert title="Check your email">
            If that address has an account, the reset link is on its way.{' '}
            <Link className="underline" to="/signin">
              Back to sign in
            </Link>
          </Alert>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            {token ? (
              <Field id="reset-password" label="New password" hint="At least 8 characters.">
                <Input
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </Field>
            ) : (
              <Field id="reset-email" label="Email address">
                <Input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Field>
            )}
            <Button type="submit" loading={busy} full>
              {token ? 'Save new password' : 'Send reset link'}
            </Button>
          </form>
        )}
      </Card>
    </FocusShell>
  );
};
